import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  WORD_LENGTH,
  mergeKeyboardStates,
  type GuessEntry,
  type LetterState,
  type TimedGuessResponse,
  type TimedRunResult,
  type TimedRunResultResponse,
  type TimedRunState,
} from "@mk/shared";
import { ApiError, api, post } from "../../lib/api.ts";
import { recordCompletedGame } from "../../lib/adGate.ts";
import { playSound } from "../../lib/sound.ts";
import { messages } from "../../i18n/index.ts";
import type { DraftCells } from "../boardDraft.ts";
import { teamMatchClock, type TeamMatchClock } from "../team/teamCountdown.ts";
import { useBoardInput } from "../useBoardInput.ts";

/**
 * Tek başına 3 dakika koşusu. Süre, kelimeler ve skor sunucuda; burası yalnızca
 * gösterir ve tahminleri iletir. Saat, soketten bağımsız olsun diye REST
 * yanıtlarındaki sunucu zamanıyla düzeltilir.
 */

export type TimedPhase = "checking" | "idle" | "starting" | "live" | "finishing" | "result";

/** Biten kelime kısa süre ekranda kalır; oyuncu cevabı görsün. */
const FLASH_MS = 900;
const RESULT_POLL_MS = 700;
/** Son saniyede bulunan kelime süreyi uzatmış olabilir; bundan fazlası saat farkı değildir. */
const RESUME_MARGIN_MS = 1000;

interface Flash {
  guesses: GuessEntry[];
  answer: string;
  solved: boolean;
}

export interface TimedSoloGame {
  phase: TimedPhase;
  state: TimedRunState | null;
  clock: TeamMatchClock | null;
  result: TimedRunResult | null;
  /** Ekrandaki tahta: biten kelime gösteriliyorsa onun tahminleri. */
  rows: GuessEntry[];
  flash: Flash | null;
  cells: DraftCells;
  pinned: number | null;
  selectCell: (index: number) => void;
  keyboard: Record<string, LetterState>;
  inputDisabled: boolean;
  sending: boolean;
  toast: string | null;
  shake: boolean;
  error: string | null;
  press: (key: string) => void;
  start: () => void;
  /** Canlı koşuyu o anki skoruyla bitirir (çıkış). */
  end: () => Promise<void>;
}

export function useTimedSoloGame(enabled: boolean): TimedSoloGame {
  const [phase, setPhase] = useState<TimedPhase>("checking");
  const [state, setState] = useState<TimedRunState | null>(null);
  const [result, setResult] = useState<TimedRunResult | null>(null);
  const [flash, setFlash] = useState<Flash | null>(null);
  const [sending, setSending] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [shake, setShake] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [now, setNow] = useState(() => Date.now());

  const offsetRef = useRef(0);
  const sendingRef = useRef(false);
  const flashTimer = useRef<number | undefined>(undefined);
  const toastTimer = useRef<number | undefined>(undefined);
  const shakeTimer = useRef<number | undefined>(undefined);
  const countdownRef = useRef<number | null>(null);
  const warnedRef = useRef<string | null>(null);

  const accept = useCallback((next: TimedRunState) => {
    offsetRef.current = next.serverNow - Date.now();
    setNow(Date.now() + offsetRef.current);
    setState(next);
  }, []);

  useEffect(() => () => {
    window.clearTimeout(flashTimer.current);
    window.clearTimeout(toastTimer.current);
    window.clearTimeout(shakeTimer.current);
  }, []);

  // Açılışta yarım kalmış koşu varsa ona dön (sayfa yenilendi, uygulama arka plana gitti…).
  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    setPhase("checking");
    api<{ state: TimedRunState | null }>("/api/solo/timed/current")
      .then(({ state: current }) => {
        if (cancelled) return;
        if (current) { accept(current); setPhase("live"); } else setPhase("idle");
      })
      .catch(() => { if (!cancelled) setPhase("idle"); });
    return () => { cancelled = true; };
  }, [accept, enabled]);

  const live = phase === "live";
  useEffect(() => {
    if (!live) return;
    const id = window.setInterval(() => setNow(Date.now() + offsetRef.current), 200);
    return () => window.clearInterval(id);
  }, [live]);

  const clock = state ? teamMatchClock(state.startsAt, state.endsAt, now) : null;
  // Biten kelime ekrandayken sonuca geçilmez: oyuncu son tahtayı ve cevabı görsün.
  const timeUp = live && clock !== null && clock.started && clock.remainingMs <= 0 && flash === null;

  // Geri sayım ve son 30 saniye sesleri.
  useEffect(() => {
    if (!live || !clock || !state) return;
    if (clock.countdownSecond !== countdownRef.current) {
      countdownRef.current = clock.countdownSecond;
      if (clock.countdownSecond !== null && document.visibilityState === "visible") playSound("countdown");
    }
    // Bulunan kelime süreyi 30 saniyenin üstüne çıkarırsa uyarı yeniden çalabilsin.
    if (clock.remainingMs > 30_000) warnedRef.current = null;
    if (clock.started && clock.remainingMs <= 30_000 && clock.remainingMs > 0 && warnedRef.current !== state.runId) {
      warnedRef.current = state.runId;
      playSound("warning-30");
    }
  }, [clock, live, state]);

  // Süre bitince sonucu sunucudan al; sunucu saati birkaç yüz ms geride olabilir.
  const runId = state?.runId;
  useEffect(() => {
    if (!timeUp || !runId) return;
    setPhase("finishing");
  }, [runId, timeUp]);

  useEffect(() => {
    if (phase !== "finishing" || !runId) return;
    let cancelled = false;
    let timer: number | undefined;
    const poll = () => {
      api<TimedRunResultResponse>(`/api/solo/timed/${runId}/result`)
        .then((response) => {
          if (cancelled) return;
          if (response.finished) {
            setResult(response.result);
            setPhase("result");
            recordCompletedGame(`timed:${response.result.runId}`);
            return;
          }
          accept(response.state);
          if (response.state.endsAt - response.state.serverNow > RESUME_MARGIN_MS) {
            setPhase("live");
            return;
          }
          timer = window.setTimeout(poll, RESULT_POLL_MS);
        })
        .catch(() => { if (!cancelled) timer = window.setTimeout(poll, RESULT_POLL_MS * 2); });
    };
    poll();
    return () => { cancelled = true; window.clearTimeout(timer); };
  }, [accept, phase, runId]);

  const warn = useCallback((message: string, shakeBoard = true) => {
    setToast(message);
    window.clearTimeout(toastTimer.current);
    toastTimer.current = window.setTimeout(() => setToast(null), 1600);
    if (!shakeBoard) return;
    setShake(true);
    window.clearTimeout(shakeTimer.current);
    shakeTimer.current = window.setTimeout(() => setShake(false), 440);
  }, []);

  const resync = useCallback(() => {
    api<{ state: TimedRunState | null }>("/api/solo/timed/current")
      .then(({ state: current }) => {
        if (current) accept(current);
        else setPhase("finishing");
      })
      .catch(() => {});
  }, [accept]);

  const submit = useCallback((word: string | null) => {
    if (!state || sendingRef.current) return;
    if (word === null) {
      warn(messages.feedback.lengthRequired(WORD_LENGTH));
      return;
    }
    sendingRef.current = true;
    setSending(true);
    const submitted = state;
    post<TimedGuessResponse>(`/api/solo/timed/${submitted.runId}/guess`, {
      word, wordIndex: submitted.wordIndex, row: submitted.guesses.length,
    })
      .then((response) => {
        if (response.completed) {
          const guesses = [...submitted.guesses, { word, pattern: response.pattern }];
          if (response.solved) playSound("solved");
          setFlash({ guesses, answer: response.answer ?? "", solved: response.solved });
          window.clearTimeout(flashTimer.current);
          flashTimer.current = window.setTimeout(() => setFlash(null), FLASH_MS);
        }
        accept(response.state);
      })
      .catch((failure: unknown) => {
        if (!(failure instanceof ApiError)) {
          warn(messages.feedback.guessConnectionFailed, false);
          return;
        }
        if (failure.code === "time_expired") { setPhase("finishing"); return; }
        if (failure.code === "stale_guess") { resync(); return; }
        warn(failure.message, failure.code === "word_not_in_list" || failure.code === "invalid_word_length");
      })
      .finally(() => {
        sendingRef.current = false;
        setSending(false);
      });
  }, [accept, resync, state, warn]);

  const inputDisabled = !live || !clock?.started || timeUp || flash !== null || sending;
  const input = useBoardInput({ disabled: inputDisabled, onSubmit: submit });

  // Kabul edilen her tahminde ya da yeni kelimede aktif satır boşalsın.
  const identity = state ? `${state.runId}:${state.wordIndex}:${state.guesses.length}` : "";
  const clearDraft = input.clear;
  useEffect(() => { clearDraft(); }, [identity, clearDraft]);

  const start = useCallback(() => {
    if (phase === "starting") return;
    setError(null);
    setPhase("starting");
    setResult(null);
    setFlash(null);
    countdownRef.current = null;
    post<TimedRunState>("/api/solo/timed/start")
      .then((next) => { accept(next); setPhase("live"); })
      .catch(() => { setError(messages.solo.timedStartFailed); setPhase("idle"); });
  }, [accept, phase]);

  const end = useCallback(async () => {
    if (!state || phase !== "live") return;
    await post(`/api/solo/timed/${state.runId}/end`).catch(() => {});
  }, [phase, state]);

  const rows = flash?.guesses ?? state?.guesses ?? [];
  const keyboard = useMemo(
    () => rows.reduce<Record<string, LetterState>>((acc, row) => mergeKeyboardStates(acc, row.word, row.pattern), {}),
    [rows],
  );

  return {
    phase, state, clock, result, rows, flash,
    cells: input.cells, pinned: input.pinned, selectCell: input.selectCell,
    keyboard, inputDisabled, sending, toast, shake, error,
    press: input.press, start, end,
  };
}
